<template>
    <Drawer
        ref="drawer"
        :title="isCreating ? 'Create Subcategory' : 'Update Subcategory'"
        :loading="isLoading || isUpdating"
        paddingless
        @closed="resetForm()"
    >
        <Tabs
            v-if="category"
            :tabs="
                [
                    {
                        title: 'General',
                        name: 'general',
                    },
                    isCostCategory
                        ? {
                              title: 'Calculated Totals',
                              name: 'calculations',
                          }
                        : null,
                    xeroIntegration
                        ? {
                              title: 'Xero',
                              name: 'xero',
                          }
                        : null,
                    acumaticaIntegration
                        ? {
                              title: 'Acumatica',
                              name: 'acumatica',
                          }
                        : null,
                ].filter((t) => t)
            "
        >
            <template #heading(xero)>
                <div class="tw-flex tw-items-center tw-gap-2">
                    <img
                        src="@/images/brands/xero.svg"
                        class="tw-w-4 tw-h-4"
                        :class="{
                            'tw-grayscale': !category.xero_account_id,
                        }"
                        alt="Xero"
                    />
                    Xero
                </div>
            </template>
            <template #heading(acumatica)>
                <div class="tw-flex tw-items-center tw-gap-2">
                    <img
                        src="@/images/brands/acumatica-icon.svg"
                        class="tw-w-4 tw-h-4"
                        :class="{
                            'tw-grayscale': !category.acumatica_account_id,
                        }"
                        alt="Acumatica"
                    />
                    Acumatica
                </div>
            </template>
            <template #tab(general)>
                <div class="tw-p-4 tw-grid tw-gap-3">
                    <FormField label="Name">
                        <Textbox
                            v-model="category.name"
                            placeholder="Please insert a name here"
                            maxlength="255"
                            required
                        />
                    </FormField>
                    <FormField label="Description">
                        <Textbox
                            v-model="category.description"
                            placeholder="Please insert a description here"
                            maxlength="255"
                        />
                    </FormField>
                    <FormField v-if="isCreating" label="Department">
                        <Select
                            v-model="category.department_id"
                            placeholder="Select a Department"
                            :loading="isLoading"
                            class="tw-w-full"
                        >
                            <option v-for="department in departments" :key="department.id" :value="department.id">
                                {{ department.name }}
                            </option>
                        </Select>
                    </FormField>
                    <FormField label="Users" class="tw-bg-gray-100 tw-border tw-rounded tw-p-3 tw-mb-2">
                        <template #label>
                            <div class="tw-flex">
                                <div class="tw-mr-auto">Users</div>
                                <Button compact @click.prevent="toggleAllUsers()">
                                    {{
                                        category.users.length === availableUsers.length ? 'Deselect all' : 'Select all'
                                    }}
                                </Button>
                            </div>
                        </template>
                        <Checkbox
                            v-for="availableUser in availableUsers"
                            :key="availableUser.id"
                            v-model="category.users"
                            :value="availableUser.id"
                        >
                            {{ availableUser.name }}
                        </Checkbox>
                    </FormField>
                    <FormField label="Events" class="tw-bg-gray-100 tw-border tw-rounded tw-mb-2 tw-p-3">
                        <template #label>
                            <div class="tw-flex">
                                <div class="tw-mr-auto">Events</div>
                                <Button compact @click.prevent="toggleAllEvents()">
                                    {{
                                        category.new_events.length === availableEvents.length
                                            ? 'Deselect all'
                                            : 'Select all'
                                    }}
                                </Button>
                            </div>
                        </template>
                        <div v-for="availableEvent in availableEvents" :key="availableEvent.id">
                            <Checkbox
                                v-if="!getEntries(availableEvent.id).length"
                                v-model="category.new_events"
                                :value="availableEvent.id"
                            >
                                {{ availableEvent.name }}
                            </Checkbox>
                            <Tooltip
                                v-else
                                :label="
                                    'This subcategory contains active entries and cannot be unassigned (' +
                                    getEntries(availableEvent.id) +
                                    ')'
                                "
                            >
                                <Checkbox disabled>
                                    {{ availableEvent.name }}
                                </Checkbox>
                            </Tooltip>
                        </div>
                    </FormField>
                </div>
            </template>
            <template #tab(calculations)>
                <div class="tw-grid tw-gap-4 tw-p-4">
                    <div class="tw-flex tw-items-start tw-justify-between tw-gap-3">
                        <div>
                            <h4 class="tw-m-0 tw-text-sm tw-font-medium tw-text-gray-900">Calculated totals</h4>
                            <p class="tw-mt-1 tw-text-xs tw-text-gray-500">
                                Add automatic costs based on event-wide ticket values. Every calculation is applied to
                                each event containing this subcategory.
                            </p>
                        </div>
                        <Button compact type="is-secondary" @click="addCalculation()">
                            <i class="mdi mdi-plus"></i>
                            Add
                        </Button>
                    </div>

                    <div
                        v-for="(calculation, index) in category.calculations"
                        :key="calculation.id ?? `new-${index}`"
                        class="tw-grid tw-gap-3 tw-rounded-lg tw-border tw-border-gray-200 tw-bg-gray-50 tw-p-3"
                    >
                        <FormField label="Title">
                            <Textbox v-model="calculation.name" maxlength="255" placeholder="e.g. PRS" required />
                        </FormField>

                        <div class="tw-grid tw-grid-cols-2 tw-gap-3">
                            <FormField label="Based on">
                                <Select
                                    v-model="calculation.source"
                                    required
                                    @change="normaliseCalculationMethod(calculation)"
                                >
                                    <option
                                        v-for="source in calculationSources"
                                        :key="source.value"
                                        :value="source.value"
                                    >
                                        {{ source.label }}
                                    </option>
                                </Select>
                            </FormField>
                            <FormField label="Calculation">
                                <Select v-model="calculation.method" required>
                                    <option
                                        v-for="method in calculationMethodsFor(calculation)"
                                        :key="method.value"
                                        :value="method.value"
                                    >
                                        {{ method.label }}
                                    </option>
                                </Select>
                            </FormField>
                        </div>

                        <FormField :label="calculation.method === 'percentage' ? 'Percentage' : 'Charge per unit'">
                            <NumberInput
                                v-model="calculation.rate"
                                :min="0"
                                :max="calculation.method === 'percentage' ? 100 : 1000000"
                                :step="0.01"
                                :dp="4"
                            >
                                <template v-if="calculation.method === 'percentage'" #prepend>%</template>
                            </NumberInput>
                        </FormField>

                        <div class="tw-flex tw-justify-end">
                            <Button
                                compact
                                square
                                type="is-danger"
                                title="Remove calculation"
                                @click="removeCalculation(index)"
                            >
                                <i class="mdi mdi-delete-outline"></i>
                            </Button>
                        </div>
                    </div>

                    <div
                        v-if="!category.calculations.length"
                        class="tw-rounded-lg tw-border tw-border-dashed tw-border-gray-300 tw-p-6 tw-text-center tw-text-sm tw-text-gray-400"
                    >
                        This subcategory has no calculated totals.
                    </div>
                </div>
            </template>
            <template #tab(xero)>
                <div class="tw-grid tw-gap-3 tw-p-4">
                    <FormField label="Xero Account">
                        <Select
                            v-if="xeroAccounts.length"
                            v-model="category.xero_account_id"
                            placeholder="Select a Xero Account"
                            :loading="isLoading"
                            class="tw-w-full"
                        >
                            <option v-for="xeroAccount in xeroAccounts" :key="xeroAccount.id" :value="xeroAccount.id">
                                {{ xeroAccount.name }}
                            </option>
                        </Select>
                        <p v-else class="tw-text-gray-400">You don't have any Xero Accounts available</p>
                    </FormField>

                    <div class="tw-p-4 tw-bg-slate-100 tw-rounded-md">
                        <Checkbox v-if="category.xero_account_id" v-model="category.is_pooled">
                            Pool this Subcategory
                        </Checkbox>
                        <span class="tw-block tw-text-xs tw-text-gray-500 tw-mt-2">
                            Pooled subcategories pull the total amount for this Xero account directly from the P&amp;L
                            report in Xero, consolidating it into a single entry rather than individual transactions.
                        </span>
                    </div>
                </div>
            </template>
            <template #tab(acumatica)>
                <div class="tw-p-3">
                    <FormField label="Link with Acumatica Account" class="tw-mb-3">
                        <div class="tw-flex tw-gap-2">
                            <SmartSearchDropdown
                                v-if="canUpdate"
                                v-model="category.acumatica_account_id"
                                class="tw-flex-1"
                                placeholder="Select an Acumatica account"
                                type="accounts"
                                :items="acumaticaAccounts"
                                clearable
                            >
                                <template #item="{ item }">{{ item.code }} &bull; {{ item.name }}</template>
                            </SmartSearchDropdown>
                            <Button type="is-success" :disabled="!canUpdate" @click="createSubcategory()">Save</Button>
                        </div>
                    </FormField>
                    <Banner v-if="!category.acumatica_account" class="tw-text-center">
                        This subcategory is not linked to an Acumatica account
                    </Banner>
                    <Banner v-else type="is-info" class="tw-text-center">
                        This subcategory is current linked to
                        <span class="tw-font-semibold">
                            {{ category.acumatica_account.name }} ({{ category.acumatica_account.code }})
                        </span>
                    </Banner>
                </div>
            </template>
        </Tabs>
        <template #footer>
            <Button
                type="is-primary"
                class="tw-block tw-ml-auto"
                :disabled="!category.name || !(isCreating ? category.department_id : true) || !calculationsValid"
                :loading="isLoading || isUpdating"
                @click="createSubcategory()"
            >
                {{ isCreating ? 'Create Subcategory' : 'Update Subcategory' }}
            </Button>
        </template>
    </Drawer>
</template>
<script setup>
import Drawer from '@/js/components/Drawer.vue';
import Button from '@/js/components/controls/Button.vue';
import Textbox from '@/js/components/controls/Textbox.vue';
import Banner from '@/js/components/Layout/Banner.vue';
import Select from '@/js/components/controls/Select.vue';
import Checkbox from '@/js/components/controls/Checkbox.vue';
import NumberInput from '@/js/components/controls/NumberInput.vue';
import FormField from '@/js/components/widgets/FormField.vue';
import Tooltip from '@/js/components/Tooltip.vue';
import Tabs from '@/js/components/widgets/Tabs.vue';
import SmartSearchDropdown from '@/js/components/controls/SmartSearchDropdown.vue';
import { toast } from '@/js/utils.js';
import { capturePosthogEvent } from '@/js/composables/usePosthog.js';
import { computed, onMounted, onUnmounted, ref, useTemplateRef } from 'vue';
import { useDataStore } from '@/js/stores/DataStore.js';

const isLoading = ref(true);
const isUpdating = ref(false);
const isCreating = ref(false);
const departments = ref([]);
const acumaticaAccounts = ref([]);
const xeroAccounts = ref([]);
const availableEvents = ref([]);
const availableUsers = ref([]);
const xeroIntegration = ref(useDataStore().get('account.xero_organisation'));
const acumaticaIntegration = ref(useDataStore().get('account.integrations.acumatica'));
const category = ref({
    department_id: null,
    users: [],
    new_events: [],
    calculations: [],
});
const drawer = useTemplateRef('drawer');

const selectedDepartmentType = ref(null);

const openSubcategory = (payload) => {
    resetForm();
    isCreating.value = false;
    if (payload) {
        if (!payload.hasOwnProperty('department')) {
            toast('Error Opening Subcategory', 'No department was specified when opening the subcategory', 'error');
            return;
        } else if (!payload.hasOwnProperty('category')) {
            isCreating.value = true;
            category.value.department_id = payload.department;
            selectedDepartmentType.value = payload.departmentType ?? null;
            openModal();
            return;
        }
        openModal(payload.department, payload.category);
        return;
    }
    isCreating.value = true;
    openModal();
};

onMounted(() => {
    Eventbus.$on('subcategory:open', openSubcategory);
});

onUnmounted(() => {
    Eventbus.$off('subcategory:open', openSubcategory);
});

const canUpdate = computed(() => window.Auth.can('manage subcategories'));
const calculationMethods = [
    { value: 'percentage', label: 'Percentage' },
    { value: 'per_unit', label: 'Per unit' },
];
const calculationSources = [
    { value: 'net_ticket_sales_without_rebate', label: 'Net ticket sales excluding rebate' },
    { value: 'ticket_count', label: 'Ticket count' },
    { value: 'attendee_capacity', label: 'Attendee capacity' },
];
const calculationMethodsFor = (calculation) =>
    calculation.source === 'net_ticket_sales_without_rebate'
        ? calculationMethods
        : calculationMethods.filter((method) => method.value === 'per_unit');
const normaliseCalculationMethod = (calculation) => {
    if (calculation.source !== 'net_ticket_sales_without_rebate' && calculation.method === 'percentage') {
        calculation.method = 'per_unit';
    }
};
const isCostCategory = computed(() => {
    const department =
        category.value.department ?? departments.value.find((item) => item.id === Number(category.value.department_id));
    return department?.type === 1;
});
const calculationsValid = computed(() => {
    return (category.value.calculations ?? []).every((calculation) => {
        const hasRequiredValues =
            calculation.name?.trim() &&
            calculation.method &&
            calculation.source &&
            calculation.rate !== '' &&
            calculation.rate !== null &&
            Number(calculation.rate) >= 0 &&
            (calculation.method !== 'percentage' || Number(calculation.rate) <= 100) &&
            (calculation.method !== 'percentage' || calculation.source === 'net_ticket_sales_without_rebate');

        return !!hasRequiredValues;
    });
});

const openModal = (departmentId = null, subcategoryId = null) => {
    if (isCreating.value) {
        getDepartments();
    } else if (subcategoryId) {
        getSubcategory(departmentId, subcategoryId);
    }
    if (acumaticaIntegration.value) getAcumaticaAccounts();
    if (xeroIntegration.value) getXeroAccounts();
    getEvents();
    getUsers();
    drawer.value?.open();
};

const resetForm = () => {
    isLoading.value = true;
    isCreating.value = false;
    isUpdating.value = false;
    category.value = {
        users: [],
        new_events: [],
        calculations: [],
    };
    selectedDepartmentType.value = null;
    xeroAccounts.value = [];
};

const closeModal = () => {
    resetForm();
    drawer.value?.close();
};

const getDepartments = () => {
    isLoading.value = true;
    axios
        .get(route('api.account.departments'))
        .then((response) => (departments.value = response.data))
        .finally(() => (isLoading.value = false));
};

const getSubcategory = (departmentId, subcategoryId) => {
    isLoading.value = true;
    axios
        .get(
            route('api.account.departments.categories.get', {
                department: departmentId,
                category: subcategoryId,
            })
        )
        .then((response) => {
            category.value = {
                ...response.data,
                users: response.data.users
                    .map((u) => u.id)
                    .filter((id) => availableUsers.value.some((au) => au.id === id)),
                new_events: response.data.events.map((e) => e.id),
                calculations: response.data.calculations ?? [],
            };
        })
        .finally(() => (isLoading.value = false));
};

const getUsers = () => {
    axios.get(route('api.account.team')).then((response) => (availableUsers.value = response.data));
};

const getEvents = () => {
    axios.get(route('api.account.event.allevents')).then((response) => (availableEvents.value = response.data));
};

const getAcumaticaAccounts = () => {
    if (!acumaticaIntegration.value) return;
    axios
        .get(route('api.account.integrations.acumatica.accounts'))
        .then((response) => (acumaticaAccounts.value = response.data));
};

const getXeroAccounts = () => {
    if (!xeroIntegration.value) return;
    axios.get(route('api.account.xero.accounts')).then((response) => (xeroAccounts.value = response.data));
};

const createSubcategory = () => {
    isUpdating.value = true;
    const wasCreating = isCreating.value;
    const departmentType = Number(
        departments.value.find((department) => Number(department.id) === Number(category.value.department_id))?.type ??
            selectedDepartmentType.value
    );
    const endpoint = wasCreating
        ? route('api.account.departments.categories.create', {
              department: category.value.department_id,
          })
        : route('api.account.departments.categories.update', {
              department: category.value.department_id,
              category: category.value.id,
          });
    axios
        .post(endpoint, category.value)
        .then(() => {
            if (wasCreating && departmentType === 2) {
                capturePosthogEvent('revenue_subcategory_created');
            } else if (wasCreating && departmentType === 1) {
                capturePosthogEvent('cost_subcategory_created');
            }
            closeModal();
            toast('Success', 'Your subcategory has been ' + (isCreating.value ? 'created' : 'updated'), 'success');
            Eventbus.$emit('reload:departments');
        })
        .finally(() => (isUpdating.value = false));
};

const addCalculation = () => {
    category.value.calculations ??= [];
    category.value.calculations.push({
        name: '',
        method: 'percentage',
        source: 'net_ticket_sales_without_rebate',
        rate: 0,
    });
};

const removeCalculation = (index) => {
    category.value.calculations.splice(index, 1);
};

const getEntries = (eventId) => {
    if (!category.value || !category.value.new_events || !category.value.new_events.length) {
        return false;
    }
    const event = category.value.events?.find((availableEvent) => availableEvent.id === eventId);
    if (!event) {
        return [];
    }
    const messages = [];
    if (
        !(
            event.budget_entries_count ||
            event.purchase_orders_count ||
            event.revenue_entries_count ||
            event.ticket_types_count
        )
    ) {
        return false;
    }
    if (event.budget_entries_count) {
        messages.push(event.budget_entries_count + ' budget entr' + (event.budget_entries_count === 1 ? 'y' : 'ies'));
    }
    if (event.purchase_orders_count) {
        messages.push(event.purchase_orders_count + ' purchase order' + (event.purchase_orders_count === 1 ? '' : 's'));
    }
    if (event.revenue_entries_count) {
        messages.push(
            event.revenue_entries_count + ' revenue entr' + (event.revenue_entries_count === 1 ? 'y' : 'ies')
        );
    }
    if (event.ticket_types_count) {
        messages.push(event.ticket_types_count + ' ticket type' + (event.ticket_types_count === 1 ? '' : 's'));
    }
    return messages.join(', ');
};

const toggleAllUsers = () => {
    if (!category.value.users || category.value.users.length === availableUsers.value.length) {
        category.value.users = [];
    } else {
        category.value.users = availableUsers.value.map((u) => u.id);
    }
};

const toggleAllEvents = () => {
    if (!category.value.new_events || category.value.new_events.length === availableEvents.value.length) {
        category.value.new_events = [];
    } else {
        category.value.new_events = availableEvents.value.map((e) => e.id);
    }
};
</script>
